/**
 * Entry point.
 *
 * Kept deliberately thin: resolve the selected catalog entry, lazily load its
 * JSON, build the Game, start it. Gameplay ordering lives in Game.fixedStep().
 */

import { Game } from '@/game/game';
import { LEVEL_CATALOG } from '@/levels/catalog';
import { resolveLevelRequest, type LevelRequestReason } from '@/systems/levelUnlocks';
import { createSaveStore } from '@/engine/storage';
import { LevelSelect } from '@/ui/levelSelect';

const CONTROLS = [
  'WASD / arrows — move',
  'Mouse — look (click to capture)',
  'Shift — dash',
  'Hold left click — charge pounce',
  'Space — jump',
  'Ctrl / C — sneak (Silent Paws)',
  'E / right click — interact, pick up, drop',
  'F1 — level editor (dev)',
  '` — debug panel',
].join('\n  ');

async function boot(): Promise<void> {
  const canvas = document.getElementById('game-canvas');
  const uiRoot = document.getElementById('ui-root');
  const bootScreen = document.getElementById('boot-screen');
  const status = bootScreen?.querySelector('.boot-status');

  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error('#game-canvas is missing from index.html');
  }
  if (!(uiRoot instanceof HTMLElement)) throw new Error('#ui-root is missing from index.html');

  try {
    const saves = createSaveStore();
    const save = saves.load();
    const requestedId = new URL(window.location.href).searchParams.get('level');
    const catalog = LEVEL_CATALOG.map((entry) => ({
      ...entry,
      available: entry.load !== undefined,
    }));
    const request = resolveLevelRequest(catalog, requestedId, save);

    if (!request.entry) {
      new LevelSelect(uiRoot, {
        levels: LEVEL_CATALOG,
        save,
        ...(request.reason === 'none' ? {} : { message: messageFor(request.reason) }),
        onSelect: navigateToLevel,
      });
      bootScreen?.classList.add('hidden');
      return;
    }

    const load = request.entry.load;
    if (!load) throw new Error(`Level "${request.entry.id}" has no data loader`);
    if (status) status.textContent = 'Loading physics…';
    const levelData = await load();
    const game = await Game.create(canvas, levelData, saves, request.entry.fileName);

    if (status) status.textContent = 'Stretching…';
    game.start();

    // Expose for the Playwright smoke test and for poking at state in the
    // console. Dev/debug only - nothing in the game reads this.
    (window as unknown as { catGame: Game }).catGame = game;

    bootScreen?.classList.add('hidden');
    console.info(`[cat-game] ready\n  ${CONTROLS}`);
  } catch (error) {
    console.error('[cat-game] failed to boot', error);
    if (status) {
      status.textContent = `Something broke: ${error instanceof Error ? error.message : String(error)}`;
    }
    throw error;
  }
}

function navigateToLevel(levelId: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('level', levelId);
  window.location.assign(url.toString());
}

function messageFor(reason: Exclude<LevelRequestReason, 'none'>): string {
  if (reason === 'locked') return 'Finish the room before it first!';
  if (reason === 'unavailable') return 'That room is still being built.';
  return 'That room wandered off. Pick another!';
}

void boot();
